import {
  ComponentProps,
  ComponentType,
  FC,
  ReactNode,
  createRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TinderCard from 'react-tinder-card';

import { FeedPost, useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById, useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';

interface SwiperScreenContentProps<T> {
  isLoading: boolean;
  cardData: T[];
  containerHeight: number;
  CardContent: ComponentType<{
    data: T;
    cardHeight: number;
    onCardContentInteraction: () => void;
  }>;
  onSwipeRight: (data: T) => void;
  onSwipeLeft: (data: T) => void;
  emptyState: ReactNode;
}

let canClickTimer: NodeJS.Timeout | null = null;

const SwiperScreenContent = <T extends { key: string }>({
  isLoading,
  cardData,
  containerHeight,
  CardContent,
  onSwipeRight,
  onSwipeLeft,
  emptyState,
}: SwiperScreenContentProps<T>) => {
  const [canClick, setCanClick] = useState(true);
  const [swipedCards, setSwipedCards] = useState<Record<string, boolean>>({});
  const cardRefs = useMemo(
    () => Object.fromEntries(cardData.map(({ key }) => [key, createRef<any>()])),
    [cardData],
  );

  const handleCardLeftScreen = (data: T) => {};

  const handleSwipe = (direction: string, data: T) => {
    if (canClickTimer) {
      clearTimeout(canClickTimer);
    }

    if (direction === 'right') {
      onSwipeRight(data);
    } else if (direction === 'left') {
      onSwipeLeft(data);
    }

    setSwipedCards((prev) => ({ ...prev, [data.key]: true }));
    setCanClick(false);

    canClickTimer = setTimeout(() => {
      setCanClick(true);
    }, 2000);
  };

  const remainingCards = useMemo(
    () => cardData.filter(({ key }) => !swipedCards[key]),
    [cardData, swipedCards],
  );
  const topCard = remainingCards?.[remainingCards?.length - 1];

  const handleSkip = () => {
    cardRefs[topCard?.key ?? '']?.current?.swipe('left');
  };

  const handleLike = () => {
    cardRefs[topCard?.key ?? '']?.current?.swipe('right');
  };

  const VERTICAL_PADDING = 16 * 2;
  const ICON_BUTTON_CONTAINER = 48;
  const EXTRA_PADDING = 16;

  if (isLoading) {
    return (
      <div className="p-3">
        <Skeleton height={400} />
      </div>
    );
  }

  return (
    <div
      style={{
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        padding: '1rem 0',
        display: 'grid',
      }}
    >
      {remainingCards.length ? (
        <>
          <div>
            {remainingCards.map((data) => (
              <div
                key={data.key}
                style={{ position: 'absolute', width: '100%', padding: '0 1rem' }}
              >
                <TinderCard
                  ref={cardRefs[data.key]}
                  onSwipe={(direction) => handleSwipe(direction, data)}
                  onCardLeftScreen={() => handleCardLeftScreen(data)}
                  preventSwipe={['up', 'down']}
                >
                  <CardContent
                    data={data}
                    cardHeight={
                      containerHeight - VERTICAL_PADDING - ICON_BUTTON_CONTAINER - EXTRA_PADDING
                    }
                    onCardContentInteraction={() => {
                      setSwipedCards((prev) => ({ ...prev, [data.key]: true }));
                    }}
                  />
                </TinderCard>
              </div>
            ))}
          </div>
          <div className="mt-auto">
            <div className="d-flex gap-3 justify-content-center">
              <div>
                <IconButton
                  disabled={!canClick}
                  color="neutral"
                  variant="smooth"
                  size="lg"
                  onClick={handleSkip}
                >
                  <div style={{ color: 'var(--color-red)' }}>
                    <FontAwesomeIcon name="times" />
                  </div>
                </IconButton>
              </div>
              <div>
                <IconButton
                  disabled={!canClick}
                  color="neutral"
                  variant="smooth"
                  size="lg"
                  onClick={handleLike}
                >
                  <div style={{ color: 'var(--color-green)' }}>
                    <FontAwesomeIcon name="heart" faStyle="far" />
                  </div>
                </IconButton>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="px-3">{emptyState}</div>
      )}
    </div>
  );
};

const LookCardContent: FC<{
  data: FeedPost & { key: string };
  cardHeight: number;
  onCardContentInteraction: () => void;
}> = ({ data: post, cardHeight, onCardContentInteraction }) => {
  const [hasError, setHasError] = useState(false);
  const TITLE_CONTAINER_HEIGHT = 80;
  const BADGE_CONTAINER_HEIGHT = 115;

  return (
    <div>
      <Card>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            height: cardHeight - TITLE_CONTAINER_HEIGHT - BADGE_CONTAINER_HEIGHT,
            overflow: 'hidden',
            position: 'relative',
            padding: '1rem',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${post.entertainerMedia.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              filter: 'blur(8px)',
              opacity: 0.8,
            }}
          />
          <AsyncImage
            style={{
              height: '100%',
              width: '100%',
              position: 'relative',
              zIndex: 1,
              objectFit: 'contain',
              display: 'block',
              borderRadius: 8,
            }}
            src={post.entertainerMedia.image}
            onError={() => {
              console.log('error');
            }}
          />
        </div>
        <div
          className="d-flex align-items-center py-3 px-2"
          style={{ height: TITLE_CONTAINER_HEIGHT }}
        >
          <div>
            <p
              className="my-0"
              style={{
                textTransform: 'uppercase',
                letterSpacing: '.1rem',
                fontWeight: 500,
                opacity: 0.7,
                fontSize: '.7rem',
              }}
            >
              {post.entertainer?.title}
            </p>
            <h3 className="my-0">{post.entertainerMedia?.title}</h3>
          </div>
        </div>
        <SendEntertainerBadgeButton
          primaryBadgeIds={post.primaryBadgeIds}
          entertainerId={post.entertainer._id}
          entertainerMediaId={post.entertainerMedia._id}
          onAfterSend={onCardContentInteraction}
        />
      </Card>
    </div>
  );
};

interface EntertainerLooksSwiperScreenProps {
  toolbarButtonMode?: string;
}

interface EntertainerLooksSwiperScreenQueryParams {
  cityId?: string;
  filter?: string;
}

export const EntertainerLooksSwiperScreen = createScreenComponent<
  EntertainerLooksSwiperScreenProps,
  EntertainerLooksSwiperScreenQueryParams
>(({ toolbarButtonMode, f7route }) => {
  const screenContentRef = useRef<{ el: HTMLElement | null }>(null);
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { currentCity: currentUserCity } = useCurrentUserCity();
  const cityId = f7route?.query?.cityId ?? currentUserCity?._id ?? '';
  const { city } = useCityById(cityId);
  const currentCity = city ?? currentUserCity;
  const { postsAreReady, posts = [] } = useFeaturedLooksFeedPostsQuery({
    cityId,
    filter: f7route?.query?.filter,
  });
  const { sendEntertainerBadgeAsync, isSubmitting } = useSendEntertainerBadge();

  const [countdown, setCountdown] = useState(10);
  useEffect(() => {
    let interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 0) {
          clearInterval(interval);
        }

        return Math.max(0, prev - 1);
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (postsAreReady && !posts.length) {
      const queryParams = new URLSearchParams({
        cityId: currentCity?._id ?? '',
      });

      if (f7route?.query?.filter) {
        queryParams.append('filter', f7route.query.filter);
      }

      navigate(`/noauth/featured-looks?${queryParams.toString()}`);
    }
  }, [postsAreReady]);

  const [containerHeight, setContainerHeight] = useState<number>();
  useLayoutEffect(() => {
    if (screenContentRef.current?.el?.clientHeight) {
      const { firstChild } = screenContentRef.current.el;

      if (firstChild instanceof HTMLElement) {
        setContainerHeight(firstChild.clientHeight);
      }
    }
  }, []);

  const homeText = `See ${currentCity?.name} Activity`;
  const seeAllText = `${toolbarButtonMode === 'home' ? homeText : 'See All Looks'}${countdown > 0 ? ` (in ${countdown}s)` : ''}`;
  const canSeeAll = countdown === 0;

  const cardData = useMemo(() => {
    return posts.map((post) => ({ ...post, key: post.entertainerMedia._id }));
  }, [posts]);

  const handleSwipe = (direction: string) => (post: FeedPost & { key: string }) => {
    track(`Swipe ${direction}`, {
      entertainerMediaId: post.entertainerMedia._id,
    });

    sendEntertainerBadgeAsync({
      entertainerId: post.entertainer._id,
      entertainerMediaId: post.entertainerMedia._id,
      reason: direction === 'right' ? 'Look' : 'skip',
    });
  };

  return (
    <Screen
      name="Entertainer Looks Swiper Screen"
      hideToolbar
      disableLocationPermissionGate={false}
      headerProps={{
        title: 'Support Entertainers',
      }}
      bottomToolbar={
        <Screen.BottomToolbar maxWidth={425}>
          <Button
            disabled={!canSeeAll}
            color="neutral"
            variant="smooth"
            roundness="rounded"
            onClick={() => {
              const queryParams = new URLSearchParams({
                cityId: currentCity?._id ?? '',
              });

              if (f7route?.query?.filter) {
                queryParams.append('filter', f7route.query.filter);
              }

              navigate(
                toolbarButtonMode === 'home'
                  ? `/`
                  : `/noauth/featured-looks?${queryParams.toString()}`,
              );
            }}
          >
            {seeAllText}
          </Button>
        </Screen.BottomToolbar>
      }
    >
      <Screen.Content ref={screenContentRef} maxWidth={425} screenContentClassName="p-0">
        <SwiperScreenContent
          isLoading={!postsAreReady}
          cardData={cardData}
          containerHeight={containerHeight}
          CardContent={LookCardContent}
          onSwipeLeft={handleSwipe('left')}
          onSwipeRight={handleSwipe('right')}
          emptyState={
            <EmptyState
              title="No more looks"
              text={`You have swiped through all the looks${f7route?.query?.filter ? ' for these entertainers' : ''}.`}
            />
          }
        />
      </Screen.Content>
    </Screen>
  );
});
